/**
 * MediaLibraryDrawer — right-side panel listing chat media in tabs.
 *
 * Owner-spec 2026-05-31 (Phase 1): WhatsApp-style \"מדיה / מסמכים / קישורים\"
 * library so the agent can scan a conversation's attachments without
 * scrolling the timeline. Tap a thumbnail → opens the lightbox at the
 * matching index in the parent media list.
 *
 * Tab buckets:
 *   media     — image + video (grid of thumbnails)
 *   documents — anything non-image/video/audio with a media URL
 *   links     — URLs extracted from message bodies via regex
 *
 * Stays a controlled component: parent passes `open`, `onClose`,
 * `messages`, `onMediaClick(index)`. Drawer doesn't own data — just
 * derives + renders.
 */

import React, { useState, useMemo } from 'react';

const URL_RE = /(https?:\/\/[^\s<>"']+)/gi;

const extractLinks = (messages) => {
  const items = [];
  (messages || []).forEach(m => {
    const body = m.body || '';
    let match;
    URL_RE.lastIndex = 0;
    while ((match = URL_RE.exec(body)) !== null) {
      items.push({
        url: match[1],
        ts: m.timestamp,
        senderName: m.sentByName || (m.fromMe ? 'נציג' : 'לקוח'),
      });
    }
  });
  // newest first
  return items.sort((a, b) => (b.ts || 0) - (a.ts || 0));
};

const groupByDay = (items, tsKey = 'ts') => {
  const today = new Date();
  const todayStr = today.toLocaleDateString('he-IL');
  const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('he-IL');
  const groups = new Map();
  items.forEach(it => {
    const ts = it[tsKey];
    const d = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
    if (isNaN(d.getTime())) {
      const k = 'אחר';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(it);
      return;
    }
    let label = d.toLocaleDateString('he-IL');
    if (label === todayStr) label = 'היום';
    else if (label === yesterday) label = 'אתמול';
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(it);
  });
  return Array.from(groups.entries());
};

const formatBytes = (bytes) => {
  if (!bytes || isNaN(bytes)) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const MediaLibraryDrawer = ({
  open,
  onClose,
  messages,
  onMediaClick,
}) => {
  const [tab, setTab] = useState('media');

  const mediaItems = useMemo(() => (
    (messages || [])
      .map((m, idx) => ({ m, idx }))
      .filter(({ m }) => {
        const t = (m.mediaType || m.type || '').toLowerCase();
        return (t === 'image' || t === 'video') && (m.mediaUrl || m.body);
      })
      .map(({ m, idx }) => ({
        id: m.id || idx,
        msgIndex: idx,
        url: m.mediaUrl || m.body,
        type: (m.mediaType || m.type || '').toLowerCase(),
        caption: m.caption || '',
        ts: m.timestamp,
      }))
      .reverse() // newest first in the grid
  ), [messages]);

  const docItems = useMemo(() => (
    (messages || [])
      .filter(m => (m.mediaType || m.type || '').toLowerCase() === 'document')
      .map(m => ({
        id: m.id,
        url: m.mediaUrl || m.body,
        filename: m.mediaFilename || 'קובץ',
        mime: m.mediaMimeType || '',
        size: m.mediaSize,
        ts: m.timestamp,
      }))
      .reverse()
  ), [messages]);

  const linkItems = useMemo(() => extractLinks(messages), [messages]);

  if (!open) return null;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.3)',
        zIndex: 9000,
        display: 'flex',
        justifyContent: 'flex-start',
      }}
    >
      <div style={{
        width: '380px',
        maxWidth: '92vw',
        height: '100%',
        background: 'white',
        boxShadow: '2px 0 12px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderBottom: '1px solid #eee',
        }}>
          <button
            onClick={onClose}
            title="סגור"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#555',
            }}
          >
            ←
          </button>
          <span style={{ fontWeight: 600, fontSize: '15px' }}>חיפוש בצ'אט / מדיה</span>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #eee',
        }}>
          {[
            { id: 'media', label: `מדיה (${mediaItems.length})` },
            { id: 'documents', label: `מסמכים (${docItems.length})` },
            { id: 'links', label: `קישורים (${linkItems.length})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flex: 1,
                background: 'none',
                border: 'none',
                padding: '12px 8px',
                fontSize: '13px',
                cursor: 'pointer',
                color: tab === t.id ? '#25D366' : '#666',
                borderBottom: tab === t.id ? '2px solid #25D366' : '2px solid transparent',
                fontWeight: tab === t.id ? 600 : 400,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
          {tab === 'media' && (
            mediaItems.length === 0 ? (
              <EmptyHint text="אין מדיה בשיחה זו" />
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '6px',
              }}>
                {mediaItems.map((it, gridIdx) => (
                  <button
                    key={it.id}
                    onClick={() => onMediaClick && onMediaClick(mediaItems.length - 1 - gridIdx)}
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      padding: 0,
                      border: 'none',
                      background: '#e7e7e7',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                    title={it.caption || ''}
                  >
                    {it.type === 'video' ? (
                      <div style={{
                        width: '100%', height: '100%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#333', color: 'white', fontSize: '24px',
                      }}>
                        ▶
                      </div>
                    ) : (
                      <img
                        src={it.url}
                        alt=""
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </button>
                ))}
              </div>
            )
          )}

          {tab === 'documents' && (
            docItems.length === 0 ? (
              <EmptyHint text="אין מסמכים בשיחה זו" />
            ) : (
              groupByDay(docItems).map(([day, rows]) => (
                <div key={day} style={{ marginBottom: '14px' }}>
                  <DayLabel label={day} />
                  {rows.map((it, i) => (
                    <a
                      key={it.id || i}
                      href={it.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px',
                        background: '#f8f8f8',
                        borderRadius: '8px',
                        marginBottom: '6px',
                        textDecoration: 'none',
                        color: 'inherit',
                      }}
                    >
                      <div style={{ fontSize: '24px' }}>📕</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#111',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {it.filename}
                        </div>
                        <div style={{ fontSize: '11px', color: '#777', marginTop: '2px' }}>
                          {it.mime || 'קובץ'}{it.size ? ` · ${formatBytes(it.size)}` : ''}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              ))
            )
          )}

          {tab === 'links' && (
            linkItems.length === 0 ? (
              <EmptyHint text="אין קישורים בשיחה זו" />
            ) : (
              groupByDay(linkItems).map(([day, rows]) => (
                <div key={day} style={{ marginBottom: '14px' }}>
                  <DayLabel label={day} />
                  {rows.map((it, i) => (
                    <a
                      key={i}
                      href={it.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        padding: '10px',
                        background: '#f8f8f8',
                        borderRadius: '8px',
                        marginBottom: '6px',
                        textDecoration: 'none',
                        color: '#1565c0',
                        wordBreak: 'break-all',
                        fontSize: '13px',
                      }}
                    >
                      {it.url}
                      <div style={{ color: '#777', fontSize: '11px', marginTop: '4px' }}>
                        {it.senderName}
                      </div>
                    </a>
                  ))}
                </div>
              ))
            )
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyHint = ({ text }) => (
  <div style={{
    padding: '60px 20px',
    textAlign: 'center',
    color: '#888',
    fontSize: '13px',
  }}>
    {text}
  </div>
);

const DayLabel = ({ label }) => (
  <div style={{
    fontSize: '11px',
    color: '#777',
    margin: '6px 0',
    fontWeight: 600,
  }}>
    {label}
  </div>
);

export default MediaLibraryDrawer;
